import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import {
    Layout,
    Switch,
    ConfigProvider,
    theme,
    Avatar,
    Dropdown,
    Menu,
    Typography,
    message,
    Select,
} from "antd";
import {
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    UserOutlined,
    LogoutOutlined,
} from "@ant-design/icons";
import SideMenu from "./SideMenu";
import { useBranch } from "./context/BranchContext";
import HeaderFavoriteTabs from "./components/HeaderFavoriteTabs";
import ClockDisplay from "./components/ClockDisplay";
import { ThemeContext } from "./context/ThemeContext";
import { routes, getKeyFromPath } from "./routes";
import ItemTypeSettingsModal from "./modules/settings/ItemTypeSettings/ItemTypeSettingsModal";

const { Header, Content, Footer } = Layout;

const MainLayout = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [itemTypeSettingsModalOpen, setItemTypeSettingsModalOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { isDark, toggleTheme, themeTokens } = useContext(ThemeContext);
    const antdAlgorithm = isDark ? theme.darkAlgorithm : theme.defaultAlgorithm;

    // URL'den selectedKey'i belirle
    const selectedKey = getKeyFromPath(location.pathname);

    const [user] = useState(() => {
        try {
            const stored = localStorage.getItem("user");
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    });

    const [loginTime] = useState(Date.now());

    // Şube yönetimi için context'ten verileri al
    const { branches, selectedBranch, handleBranchChange, loadingBranches } = useBranch();

    const handleLogout = () => {
        message.info("Oturum sonlandırıldı.");
        localStorage.removeItem("user");
        localStorage.removeItem("login-success");
        localStorage.removeItem("selectedBranch");
        localStorage.removeItem("dbName");
        localStorage.removeItem("favorites");
        window.location.href = "/login";
    };

    useEffect(() => {
        // Oturum zaman aşımı kontrolü
        let timeout;
        const resetTimer = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                message.warning("Uzun süre işlem yapılmadı. Oturum kapatılıyor.");
                handleLogout();
            }, 30 * 60000); // 30 dakika
        };

        const events = ["mousemove", "keydown", "click", "touchstart"];
        events.forEach((e) => window.addEventListener(e, resetTimer));
        resetTimer();

        return () => events.forEach((e) => window.removeEventListener(e, resetTimer));
    }, []);


    // useMemo ile sarmalandı - user değişmedikçe yeniden oluşturulmaz
    const userMenu = useMemo(() => (
        <Menu>
            <Menu.Item key="email" disabled>
                <Typography.Text strong>
                    {user.fullName || user.adminFullName || user.email || user.adminEmail || "Kullanıcı"}
                </Typography.Text>
                {user.dbName && (
                    <div>
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                            {user.dbName}
                        </Typography.Text>
                    </div>
                )}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
                Çıkış Yap
            </Menu.Item>
        </Menu>
    ), [user]);

    // useCallback ile sarmalandı
    const handleMenuClick = useCallback((key) => {
        // Modal açan özel key'ler
        if (key === "item-type-settings") {
            setItemTypeSettingsModalOpen(true);
            return;
        }

        const route = routes.find(r => r.key === key);
        if (route) {
            navigate(route.path);
        }
    }, [navigate]);

    return (
        <ConfigProvider theme={{ algorithm: antdAlgorithm, token: themeTokens }}>
            {/* Ana Grup Tanımları Modal */}
            <ItemTypeSettingsModal
                open={itemTypeSettingsModalOpen}
                onClose={() => setItemTypeSettingsModalOpen(false)}
            />

            <Layout style={{ minHeight: "100vh", display: "flex", flexDirection: "row", background: themeTokens.colorBgLayout }}>
                {/* SideMenu */}
                <SideMenu collapsed={collapsed} onMenuClick={handleMenuClick} selectedKey={selectedKey} />

                {/* Sağ taraf içerik alanı */}
                <Layout style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: "100vh" }}>
                    <Header
                        style={{
                            background: themeTokens.colorHeaderBg,
                            color: themeTokens.colorHeaderText,
                            padding: "0 16px",
                            display: "flex",
                            alignItems: "stretch",
                            justifyContent: "space-between",
                            borderBottom: `1px solid ${themeTokens.colorBorder}`,
                            height: 56,
                            minHeight: 56,
                            lineHeight: "56px",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "stretch", flex: 1, overflow: "hidden" }}>
                            <div
                                onClick={() => setCollapsed((prev) => !prev)}
                                style={{
                                    cursor: "pointer",
                                    fontSize: 18,
                                    color: themeTokens.colorPrimary,
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "0 8px",
                                }}
                            >
                                {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            </div>

                            <HeaderFavoriteTabs onTabClick={handleMenuClick} selectedKey={selectedKey} />
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            <Select
                                value={selectedBranch}
                                placeholder="Şube Seçiniz"
                                loading={loadingBranches}
                                onChange={handleBranchChange}
                                style={{ width: 150 }}
                            >
                                {branches.map((branch) => (
                                    <Select.Option key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Switch
                                checked={isDark}
                                onChange={toggleTheme}
                                checkedChildren="🌙"
                                unCheckedChildren="☀️"
                            />
                            <Dropdown overlay={userMenu} trigger={["click"]}>
                                <div style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                                    <Avatar style={{ backgroundColor: themeTokens.colorPrimary }}>
                                        <UserOutlined />
                                    </Avatar>
                                </div>
                            </Dropdown>
                        </div>
                    </Header>

                    <Content
                        style={{
                            flex: 1,
                            overflow: "auto",
                            margin: 0,
                            background: themeTokens.colorBgContainer,
                            color: themeTokens.colorText,
                            padding: 24,
                            borderRadius: themeTokens.borderRadius,
                            boxShadow: themeTokens.boxShadow,
                        }}
                    >
                        {/* React Router ile dinamik içerik yükleme */}
                        <Routes>
                            {routes.map((route) => (
                                <Route key={route.key} path={route.path} element={route.element} />
                            ))}
                            <Route
                                path="*"
                                element={
                                    <Typography.Text type="danger">
                                        Sayfa bulunamadı: {location.pathname}
                                    </Typography.Text>
                                }
                            />
                        </Routes>
                    </Content>

                    <Footer
                        style={{
                            textAlign: "center",
                            fontSize: 13,
                            background: themeTokens.colorBgContainer,
                            color: themeTokens.colorTextSecondary || themeTokens.colorText,
                            borderTop: `1px solid ${themeTokens.colorBorder}`,
                            padding: "12px 24px",
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            gap: 24,
                        }}
                    >
                        <span><b>{user.firmName || "Firma Adı"}</b></span>
                        <span><b>{user.package}</b></span>
                        <span>{user.email || "kullanici@mail.com"}</span>
                        <ClockDisplay loginTime={loginTime} />
                    </Footer>
                </Layout>
            </Layout>
        </ConfigProvider>
    );
};

export default MainLayout;
